// Plan 24b, Task 2.2 — Turn Start Banner overlay (Screen 8)
//
// Full-width banner overlay shown at the start of each player's turn.
// Delegates all DOM construction and animation to the TurnBanner component
// (src/client/ui/components/turn-banner.ts). The screen's only job is:
//   1. Look up the player from state.
//   2. Drive the banner component's show() method.
//   3. Register a GSAP tl.call() completion callback that navigates back to
//      'board-hud' when the banner's built-in timeline finishes.
//   4. Expose dispose() to kill any pending transition and clean up DOM.
//
// DEFERRAL NOTE (Plan 24c): The current implementation treats banner-end as a
// full router transition back to 'board-hud'. Plan 24c will introduce an
// "overlay-over-HUD" pattern where the turn banner renders above the persistent
// HUD without tearing it down. At that point the router.navigate call here will
// be replaced with a simpler overlay-dismiss mechanism.

import { createTurnBanner, TURN_BANNER_TOTAL_SECONDS } from '../components/turn-banner';
import type { ScreenHandle, ScreenMountContext } from './types';
import type { BoardMountPayload } from './board-payload';
import { deserializeGameState } from '@shared/protocol';

/** Extended payload — augments BoardMountPayload with the target player id. */
export type TurnStartBannerPayload = BoardMountPayload & {
  readonly bannerPlayerId: string;
};

// ─────────────────────────────────────────────────────────────────────────────
// Factory
// ─────────────────────────────────────────────────────────────────────────────

export function createTurnStartBannerScreen(
  ctx: ScreenMountContext<TurnStartBannerPayload>,
): ScreenHandle {
  const { gsapRegistry, host, router, payload } = ctx;
  const { bannerPlayerId } = payload;
  let latestPayload: TurnStartBannerPayload = payload;

  let disposed = false;

  // ── Root wrapper ─────────────────────────────────────────────────────────

  const root = document.createElement('div');
  root.className = 'screen-turn-banner';
  root.setAttribute('data-screen-id', 'turn-banner');
  root.style.cssText = [
    'position: absolute',
    'inset: 0',
    'pointer-events: none',
    'overflow: hidden',
  ].join('; ');

  // Subscribe is required per spec: every board-phase screen subscribes once
  // so future plan revisions can react to state_sync messages if needed.
  // The unsubscribe return value is held to clean up in dispose().
  const unsubscribe = payload.subscribe((msg) => {
    if (msg.type !== 'state_sync') return;
    latestPayload = {
      ...latestPayload,
      state: deserializeGameState(msg.state),
    };
  });

  // ── Player lookup ────────────────────────────────────────────────────────

  const player = payload.state.players.get(bannerPlayerId);

  if (!player) {
    console.warn(
      `[turn-start-banner] bannerPlayerId '${bannerPlayerId}' not found in state.players — ` +
        'resolving immediately with no banner rendered.',
    );
    host.appendChild(root);
    return {
      element: root,
      dispose() {
        if (disposed) return;
        disposed = true;
        unsubscribe();
        if (root.parentNode) root.parentNode.removeChild(root);
      },
    };
  }

  // ── Convert Player.color (number) to CSS hex string ──────────────────────

  const playerColor = `#${player.color.toString(16).padStart(6, '0')}`;

  // ── Build and mount the TurnBanner component ─────────────────────────────

  // The component appends the banner element to `mount` (root) when show() is
  // called. We pass root as the mount target so the banner lives inside the
  // screen's root div and is removed along with it on dispose().
  const bannerHandle = createTurnBanner({ gsapRegistry, mount: root });

  // Append root to host now (before banner is shown) so the component can
  // attach the banner element to root during show().
  host.appendChild(root);

  // ── Trigger banner animation ─────────────────────────────────────────────

  // show() calls gsapRegistry.create('turn-banner:show') internally, builds
  // the entrance/hold/exit timeline, and registers removeFromParent at the
  // exported component duration. We do NOT call setTimeout — that would race
  // the GSAP timeline.
  bannerHandle.show({
    playerName: player.name,
    playerColor,
    iconText: player.name.trim().charAt(0).toUpperCase() || 'P',
  });

  // ── Post-animation navigation via GSAP tl.call() ─────────────────────────

  // The banner component's tl.call(removeFromParent, ...) fires at exit end.
  // We register our own navigation call on the same timeline handle so it fires
  // after the banner detaches — avoiding a race with setTimeout.
  //
  // The gsapRegistry.create() call inside bannerHandle.show() is internal to
  // the component (src/client/ui/components/turn-banner.ts:211). We cannot
  // access that handle from outside. Instead we register an onComplete-style
  // callback by creating a second, sequenced registry entry that navigates after
  // the banner's timeline duration.
  //
  // Reliable approach: create a post-banner handle with a single call() at the
  // component's exported duration.
  const postHandle = gsapRegistry.create('turn-banner:screen-nav');
  postHandle.timeline.call(
    () => {
      if (!disposed) {
        router.navigate('board-hud', latestPayload);
      }
    },
    undefined,
    TURN_BANNER_TOTAL_SECONDS,
  );

  // ── dispose ──────────────────────────────────────────────────────────────

  function dispose(): void {
    if (disposed) return;
    disposed = true;

    unsubscribe();
    postHandle.dispose();
    bannerHandle.dispose();

    if (root.parentNode) {
      root.parentNode.removeChild(root);
    }
  }

  return { element: root, dispose };
}
